
import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ArrowRight, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { featuredAchievers } from '@/data/articles';

export interface SuccessStoryGridCardProps {
  id: string;
  name: string;
  title: string;
  oneLiner: string;
  image: string;
  thumbnail?: string;
  excerpt?: string;
  stats: {
    revenue?: string;
    employeeCount?: string;
    partners?: string;
    impact?: string;
  };
  achieverId?: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

const SuccessStoryGridCard: React.FC<SuccessStoryGridCardProps> = ({
  id,
  name,
  title,
  oneLiner,
  image,
  thumbnail,
  excerpt,
  stats,
  achieverId,
  className,
  style,
  onClick,
}) => {
  // Get linked achiever data if available
  const linkedAchiever = achieverId ? featuredAchievers.find(a => a.id === achieverId) : null;
  
  // Use achiever data when available, fallback to story data
  const displayName = linkedAchiever ? linkedAchiever.author.name : name;
  const displayImage = linkedAchiever ? linkedAchiever.author.avatar : image; // Use avatar for profile
  const displayRole = linkedAchiever ? linkedAchiever.title : (stats.impact || "Achiever");
  
  // Generate excerpt from oneLiner if not provided
  const displayExcerpt = excerpt || `${displayName}'s journey is a remarkable story of entrepreneurship and determination. ${oneLiner} With hard work and strategic vision, ${displayName.split(' ')[0]} has created significant impact in the industry.`;
  
  // Use different image for thumbnail: prioritize order: 1. thumbnail param, 2. achiever thumbnailUrl, 3. achiever imageUrl, 4. story image
  const thumbnailImage = thumbnail || 
                         (linkedAchiever && linkedAchiever.thumbnailUrl) ||
                         (linkedAchiever ? linkedAchiever.imageUrl : image);
  
  return (
    <Card 
      className={cn(
        "flex flex-col overflow-hidden transition-all duration-500 hover:shadow-xl group h-full",
        "border border-purple-100 hover:border-purple-200",
        "hover:translate-y-[-8px] hover:scale-[1.02]",
        "bg-gradient-to-b from-white to-purple-50/30",
        className
      )}
      style={style}
    >
      {/* Article Thumbnail with overlay and animated gradient */}
      <div className="w-full h-52 overflow-hidden relative">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-amber-500/20 z-10 
                     opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
        <div className="relative w-full h-full">
          <img 
            src={thumbnailImage} 
            alt={displayName}
            className="w-full h-full object-cover transition-transform duration-700 
                     group-hover:scale-110 filter group-hover:brightness-105"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = '/placeholder.svg';
            }}
          />
        </div>
        
        {/* Animated border */}
        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-purple-200 via-purple-400 to-amber-300
                      transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left"></div>
                      
        {/* Stats as badges (overlap image) */}
        <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-2 z-20">
          {stats.revenue && (
            <span className="text-xs bg-amber-500 text-white px-2 py-1 rounded-full font-semibold
                        shadow-sm transition-all duration-300">
              {stats.revenue}
            </span>
          )}
          
          {stats.impact && (
            <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded-full font-semibold
                        shadow-sm transition-all duration-300">
              {stats.impact}
            </span>
          )}
          
          {stats.employeeCount && (
            <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded-full font-semibold
                        shadow-sm transition-all duration-300">
              {stats.employeeCount}
            </span>
          )}
          
          {stats.partners && (
            <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full font-semibold
                        shadow-sm transition-all duration-300">
              {stats.partners}
            </span>
          )}
        </div>
      </div>
      
      {/* Content with animation */}
      <CardContent className="flex-1 flex flex-col p-4 transform transition-all duration-500">
        {/* Profile avatar with name */}
        <div className="flex items-center gap-3 mb-3">
          <Avatar className="w-10 h-10 border-2 border-purple-100">
            <AvatarImage src={displayImage} alt={displayName} />
            <AvatarFallback className="bg-purple-100 text-purple-700">{displayName.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm font-medium text-purple-700">{displayName}</p>
            <p className="text-xs text-gray-500">{displayRole}</p>
          </div>
        </div>
        
        <h3 
          className="text-xl font-bold text-gray-800 mb-1 group-hover:text-purple-700 transition-colors cursor-pointer line-clamp-2"
          onClick={onClick}
        >
          {title}
        </h3>
        <p className="text-sm font-medium text-purple-600 mb-3 transform group-hover:translate-x-1 transition-transform duration-300">{oneLiner}</p>
        
        {/* Article Excerpt with animation */}
        <div className="mb-3 overflow-hidden flex-1">
          <p className="text-gray-600 line-clamp-3 transform transition-all duration-500 group-hover:text-gray-700 text-sm">{displayExcerpt}</p>
        </div>
      </CardContent>
      
      {/* Actions Footer with View Story and View Achiever buttons */}
      <CardFooter className="px-4 pb-4 pt-0 transform transition-all duration-500 flex flex-col gap-2">
        <Button
          variant="outline"
          className="w-full border-purple-200 hover:bg-purple-50 hover:text-purple-700 
                   transition-all duration-300 group-hover:border-purple-300
                   overflow-hidden relative z-10"
          asChild
        >
          <Link to={`/success-story/${id}`} className="flex items-center justify-center gap-2 py-1">
            <span className="relative z-10 transform group-hover:translate-x-[-4px] transition-transform duration-300">
              Read Full Story
            </span>
            <ArrowRight size={16} className="relative z-10 transform translate-x-0 group-hover:translate-x-2 transition-transform duration-300" />
            
            {/* Animated background on hover */}
            <span className="absolute inset-0 bg-gradient-to-r from-purple-100 to-amber-100
                          transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500"></span>
          </Link>
        </Button>
        
        {/* Achiever link button - only show if achieverId exists */}
        {achieverId && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-gray-600 hover:text-purple-700 hover:bg-purple-50/50"
            asChild
          >
            <Link to={`/achievers?id=${achieverId}`} className="flex items-center justify-center gap-2">
              <User size={14} />
              <span>View Achiever Profile</span>
            </Link>
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default SuccessStoryGridCard;
